import { createStore } from 'zustand/vanilla';
import { persist, createJSONStorage } from 'zustand/middleware';
import { idempotencyStore } from '../idempotency/idempotency-store';

export const AUTH_STORAGE_KEY = 'flexibilitypay-auth-storage';

export type AuthRole = 'PSP_OPERATOR' | 'PSP_ADMIN' | 'PLATFORM_ADMIN';

export interface User {
  id: string;
  email: string;
  roles?: string[];
  firstName?: string;
  lastName?: string;
}

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  refreshExpiresAt: string;
  role: AuthRole;
  tenantId: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  expiresAt: string | null;
  refreshExpiresAt: string | null;
  role: AuthRole | null;
  tenantId: string | null;
  isLoggedIn: boolean;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setSession: (session: AuthSession | null) => void;
  logout: () => void;
  clearAuth: () => void;
}

const emptySession = {
  user: null,
  token: null,
  refreshToken: null,
  expiresAt: null,
  refreshExpiresAt: null,
  role: null,
  tenantId: null,
  isLoggedIn: false,
} as const;

function purgePersistedSession(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // sessionStorage bloqueado (e.g. Safari modo privado) — ignorar
  }
}

export const authStore = createStore<AuthState>()(
  persist(
    (set) => ({
      ...emptySession,
      setUser: (user) => set({ user, isLoggedIn: !!user }),
      setToken: (token) => set({ token }),
      setSession: (session) => {
        if (!session) {
          set({ ...emptySession });
          purgePersistedSession();
          return;
        }
        set({
          token: session.accessToken,
          refreshToken: session.refreshToken,
          expiresAt: session.expiresAt,
          refreshExpiresAt: session.refreshExpiresAt,
          role: session.role,
          tenantId: session.tenantId,
          isLoggedIn: true,
        });
      },
      logout: () => {
        set({ ...emptySession });
        purgePersistedSession();
        idempotencyStore.getState().clearAll();
      },
      clearAuth: () => {
        set({ ...emptySession });
        purgePersistedSession();
        idempotencyStore.getState().clearAll();
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
